import { yupResolver } from "@hookform/resolvers/yup";
import { Eye, EyeOff } from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import * as yup from "yup";

import { Button } from "@krea/shared/design-system/Button";
import { Card, CardContent } from "@krea/shared/design-system/Card";
import { ControlledWrapper } from "@krea/shared/design-system/input/controlled-wrapper";
import { FieldDescription } from "@krea/shared/design-system/input/field";
import { Input } from "@krea/shared/design-system/input/input";
import {
  InputGroup,
  InputGroupAddon,
  InputGroupButton,
  InputGroupInput,
} from "@krea/shared/design-system/input/input-group";
import { Separator } from "@krea/shared/design-system/Separator";
import { toast } from "@krea/shared/design-system/utils/toast";

import { useGetCurrentLenderCompanyQuery } from "../hooks/use-get-lender-company.query";
import { useUpdateLenderCompanyMutation } from "../hooks/use-update-lender-company.mutation";

const schema = yup.object().shape({
  webhookLenderOauthAuthUrl: yup.string().url("Invalid URL").nonNullable(),
  webhookLenderOauthClientId: yup.string().nonNullable(),
  integrationNotificationApiUrl: yup.string().url("Invalid URL").nonNullable(),
  webhookLenderOauthClientSecret: yup.string().nonNullable(),
  webhookLenderOauthScope: yup.string().nonNullable(),
  webhookLenderTokenType: yup.string().nonNullable(),
  webhookLenderApiKey: yup.string().nonNullable(),
});

type IWebhookConfigSectionFormValues = yup.InferType<typeof schema>;

export const WebhookConfigSection = () => {
  const [showOAuthSecret, setShowOAuthSecret] = useState<boolean>(false);
  const [showApiKey, setShowApiKey] = useState<boolean>(false);

  const { data: lenderCompany } = useGetCurrentLenderCompanyQuery();
  const { mutate: updateLenderCompany } = useUpdateLenderCompanyMutation();

  const form = useForm<IWebhookConfigSectionFormValues>({
    resolver: yupResolver(schema),
    defaultValues: {
      webhookLenderOauthAuthUrl: "",
      webhookLenderOauthClientId: "",
      webhookLenderOauthClientSecret: "",
      webhookLenderOauthScope: "",
      integrationNotificationApiUrl: "",
      webhookLenderTokenType: "",
      webhookLenderApiKey: "",
    },
  });
  useEffect(() => {
    const initialValues: IWebhookConfigSectionFormValues = {};

    if (lenderCompany?.integrationNotificationApiUrl) {
      initialValues.integrationNotificationApiUrl =
        lenderCompany.integrationNotificationApiUrl;
    }

    if (lenderCompany?.webhookLenderOauthAuthUrl) {
      initialValues.webhookLenderOauthAuthUrl =
        lenderCompany.webhookLenderOauthAuthUrl;
    }

    if (lenderCompany?.webhookLenderOauthClientId) {
      initialValues.webhookLenderOauthClientId =
        lenderCompany.webhookLenderOauthClientId;
    }

    if (lenderCompany?.webhookLenderApiKey) {
      initialValues.webhookLenderApiKey = lenderCompany.webhookLenderApiKey;
    }

    if (lenderCompany?.webhookLenderOauthScope) {
      initialValues.webhookLenderOauthScope =
        lenderCompany.webhookLenderOauthScope;
    }

    if (lenderCompany?.webhookLenderTokenType) {
      initialValues.webhookLenderTokenType =
        lenderCompany.webhookLenderTokenType;
    }

    if (lenderCompany?.webhookLenderOauthClientSecret) {
      initialValues.webhookLenderOauthClientSecret =
        lenderCompany.webhookLenderOauthClientSecret;
    }

    if (lenderCompany) {
      form.reset(initialValues);
    }
  }, [lenderCompany, form]);

  const handleSubmit = useCallback(() => {
    if (!lenderCompany) return;

    form.handleSubmit((data) => {
      updateLenderCompany(
        { lenderCompanyId: lenderCompany.id, data },
        {
          onSuccess: () => {
            toast.success("Webhook configuration updated successfully");
          },
          onError: () => {
            toast.error("Failed to update webhook configuration");
          },
        },
      );
    })();
  }, [form, updateLenderCompany, lenderCompany]);

  return (
    <Card>
      <CardContent className="space-y-6 pb-8">
        <div className="">
          <h1 className="text-section">Webhook Configuration</h1>
          <p className="text-base">
            Configure webhooks to receive real-time notifications about events
            in your account.
          </p>
        </div>
        <div>
          <h3 className="mb-2 text-subsection">Webhook Notification API URL</h3>
        </div>
        <ControlledWrapper
          control={form.control}
          name="integrationNotificationApiUrl"
          render={({ field }) => (
            <>
              <Input
                type="url"
                placeholder="https://your-domain.com/api/webhook"
                {...field}
                disabled={form.formState.isSubmitting}
              />
              <FieldDescription>
                The endpoint where webhook notifications will be sent.
              </FieldDescription>
            </>
          )}
        />
        <Separator />
        <div>
          <h3 className="mb-2 text-subsection">
            OAuth Authentication (Optional)
          </h3>
          <p className="mb-6 text-sm">
            Configure OAuth authentication for secure webhook delivery. If you
            don&apos;t want to use OAuth you can leave these fields blank.
          </p>

          <div className="space-y-5">
            <ControlledWrapper
              control={form.control}
              name="webhookLenderOauthAuthUrl"
              label="OAuth Authorization URL"
              render={({ field }) => (
                <>
                  <Input
                    type="url"
                    placeholder="https://auth.your-domain.com/oauth/authorize"
                    {...field}
                    disabled={form.formState.isSubmitting}
                  />
                  <FieldDescription>
                    The URL to authorize the webhook.
                  </FieldDescription>
                </>
              )}
            />

            <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
              <ControlledWrapper
                control={form.control}
                name="webhookLenderOauthClientId"
                label="OAuth Client ID"
                render={({ field }) => (
                  <>
                    <Input
                      placeholder="your-oauth-client-id"
                      autoComplete="off"
                      {...field}
                      disabled={form.formState.isSubmitting}
                    />
                    <FieldDescription>
                      The ID of the OAuth client.
                    </FieldDescription>
                  </>
                )}
              />

              <ControlledWrapper
                control={form.control}
                name="webhookLenderOauthClientSecret"
                label="OAuth Client Secret"
                render={({ field }) => (
                  <>
                    <InputGroup>
                      <InputGroupInput
                        type={showOAuthSecret ? "text" : "password"}
                        placeholder="••••••••••••••••••••••••••••••••"
                        {...field}
                        autoComplete="off"
                        disabled={form.formState.isSubmitting}
                      />
                      <InputGroupAddon align="inline-end">
                        <InputGroupButton
                          onClick={() => setShowOAuthSecret((v) => !v)}
                          aria-label="Toggle OAuth secret visibility"
                        >
                          {showOAuthSecret ? (
                            <EyeOff className="h-4 w-4" />
                          ) : (
                            <Eye className="h-4 w-4" />
                          )}
                        </InputGroupButton>
                      </InputGroupAddon>
                    </InputGroup>
                    <FieldDescription>
                      The secret of the OAuth client.
                    </FieldDescription>
                  </>
                )}
              />
            </div>

            <ControlledWrapper
              control={form.control}
              name="webhookLenderOauthScope"
              label="OAuth Scope"
              render={({ field }) => (
                <>
                  <Input
                    placeholder="read write webhooks"
                    {...field}
                    disabled={form.formState.isSubmitting}
                  />
                  <FieldDescription>
                    Space-separated list of OAuth scopes required for webhook
                    access.
                  </FieldDescription>
                </>
              )}
            />
          </div>
        </div>

        <Separator />

        <div>
          <h3 className="mb-2 text-subsection">
            Api Key Authentication (Optional)
          </h3>
          <p className="mb-6 text-sm">
            If not using OAuth, you can authenticate webhooks using an API key.
          </p>
        </div>

        <ControlledWrapper
          control={form.control}
          name="webhookLenderApiKey"
          label="Webhook API Key"
          render={({ field }) => (
            <>
              <InputGroup>
                <InputGroupInput
                  type={showApiKey ? "text" : "password"}
                  placeholder="••••••••••••••••••••••••••••••••"
                  {...field}
                  disabled={form.formState.isSubmitting}
                />
                <InputGroupAddon align="inline-end">
                  <InputGroupButton
                    onClick={() => setShowApiKey((v) => !v)}
                    aria-label="Toggle API key visibility"
                  >
                    {showApiKey ? (
                      <EyeOff className="h-4 w-4" />
                    ) : (
                      <Eye className="h-4 w-4" />
                    )}
                  </InputGroupButton>
                </InputGroupAddon>
              </InputGroup>
              <FieldDescription>
                The API key to use for authentication. This key will be sent in
                the Authorization header of webhook requests.
              </FieldDescription>
            </>
          )}
        />
        <ControlledWrapper
          control={form.control}
          name="webhookLenderTokenType"
          label="Token Type"
          render={({ field }) => (
            <>
              <Input
                placeholder="Bearer"
                {...field}
                disabled={form.formState.isSubmitting}
              />
              <FieldDescription>
                The type of token to use for authentication.
              </FieldDescription>
            </>
          )}
        />

        <Separator />
        <div className="flex justify-end">
          <Button
            type="button"
            size="lg"
            onClick={handleSubmit}
            disabled={
              form.formState.isSubmitting ||
              !form.formState.isValid ||
              !form.formState.isDirty
            }
          >
            Save Configuration
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};
